// Sous-plan 10, lot E2 : le compte de l'app en pied de la colonne de Dust (page Assistant) : avatar aux
// initiales, nom, organisation, et un menu dont chaque action est renvoyee a l'app par message.

import { AuthContext } from "@app/lib/auth/AuthContext";
import type {
  CompteNav,
  MessageAuParent,
} from "@app/lib/compta/navigation_app";
import { envoyer, useNavigationDeLApp } from "@app/lib/compta/navigation_app";
import { routeConnecteurs } from "@app/lib/compta/page_connecteurs";
import { useAppRouter } from "@app/lib/platform";
import {
  Avatar,
  CloudArrowLeftRight,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LogOut01,
  Monitor01,
  Moon01,
  Settings01,
  Sun,
} from "@dust-tt/sparkle";
import type { ComponentType } from "react";
import { useContext } from "react";

const THEMES: Array<{
  valeur: CompteNav["theme"];
  libelle: string;
  icone: ComponentType;
}> = [
  { valeur: "light", libelle: "Thème clair", icone: Sun },
  { valeur: "dark", libelle: "Thème sombre", icone: Moon01 },
  { valeur: "system", libelle: "Thème du système", icone: Monitor01 },
];

export function CompteApp({ compte }: { compte: CompteNav }) {
  const agir = (message: MessageAuParent) => envoyer(message);
  // Page Connecteurs (22/10) : ouverte a tous ; chacun y connecte ses comptes (Google, boite mail).
  // Lu sans exiger le contexte : hors d'un espace de travail (tests, pages sans espace), pas d'entree.
  const workspaceId = useContext(AuthContext)?.workspace?.sId ?? null;
  const router = useAppRouter();
  return (
    <div className="shrink-0 border-t border-border px-2 pb-2 pt-1.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Menu du compte"
            className="flex w-full items-center gap-2 rounded-xl p-2 text-left outline-none hover:bg-hover focus-visible:bg-hover"
          >
            <Avatar size="sm" name={compte.nom} isRounded />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold text-foreground">
                {compte.nom}
              </span>
              {compte.organisation && (
                <span className="truncate text-xs text-muted-foreground">
                  {compte.organisation}
                </span>
              )}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top">
          <DropdownMenuItem
            label="Paramètres"
            icon={Settings01}
            onClick={() => agir({ type: "compte", action: "reglages" })}
          />
          {workspaceId && (
            <DropdownMenuItem
              label="Connecteurs"
              icon={CloudArrowLeftRight}
              onClick={() => void router.push(routeConnecteurs(workspaceId))}
            />
          )}
          <DropdownMenuSeparator />
          {THEMES.map((t) => (
            <DropdownMenuItem
              key={t.valeur}
              label={
                compte.theme === t.valeur ? `${t.libelle} (actuel)` : t.libelle
              }
              icon={t.icone}
              onClick={() =>
                agir({ type: "compte", action: "theme", valeur: t.valeur })
              }
            />
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            label="Se déconnecter"
            icon={LogOut01}
            variant="warning"
            onClick={() => agir({ type: "compte", action: "deconnexion" })}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Le pied de la colonne de Dust : le compte de l'app, quand l'app a passe son modele.
export function PiedCompteApp() {
  const nav = useNavigationDeLApp();
  if (!nav) {
    return null;
  }
  return <CompteApp compte={nav.compte} />;
}
